// client/src/components/common/pickerLogic.ts
//
// Pure helpers behind the shared date/time pickers (ST-00204, ST-00264, ST-00294): calendar-day
// maths, the month grid and which months a calendar shows, selection bounds, range presets, the
// time chooser's grids, typed dates / times / dates with a time (the desktop popup's text
// fields), quick times, the picker's datetime-local strings and the labels the triggers, the
// bottom sheet and the desktop popup print.
//
// Every picker value is a LOCAL calendar day / wall-clock time (the pickers hand plain Dates to
// the page, which turns them into API strings with toApiDateString or a datetime-local value).
// Labels are printed in the app's format through utils/dateUtils (en-ZA, pinned to MINING_TZ):
// `asMiningWallClock` first moves the value to the instant whose SAST wall clock shows the same
// digits, so the label always reads exactly what was picked, whatever the browser's timezone.

import {
  MINING_TZ,
  formatMiningDate,
  formatMiningDateLong,
  formatMiningTime,
} from '../../utils/dateUtils';

/** Selectable window of a picker: today and earlier, today and later, or unbounded. */
export type PickerBounds = 'past' | 'future' | 'all';

// ── Calendar days ───────────────────────────────────────────────────────────

export const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

export const endOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/** The same wall-clock time `n` calendar days later (DST-safe: built from the parts). */
export const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());

/** First day of the month `n` months after `d`'s month. */
export const addMonths = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

/** Sortable calendar-day key (yyyymmdd) — compares days without their times. */
export const dayKey = (d: Date): number =>
  d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

export const sameDay = (a: Date | null | undefined, b: Date | null | undefined): boolean =>
  !!a && !!b && dayKey(a) === dayKey(b);

export const sameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** `day` moved into `month`, keeping the day of the month where that month has it. */
export const clampIntoMonth = (day: Date, month: Date): Date => {
  const last = endOfMonth(month).getDate();
  return new Date(month.getFullYear(), month.getMonth(), Math.min(day.getDate(), last));
};

/** Anything a legacy picker prop can hold, as a Date (the pickers' historical rule: empty = today). */
export const toDateOrToday = (value: Date | string | number | null | undefined): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  return new Date(value);
};

// ── Bounds ──────────────────────────────────────────────────────────────────

export interface DayLimits {
  bounds?: PickerBounds;
  /** Earliest selectable day (inclusive, compared by calendar day). */
  min?: Date | null;
  /** Latest selectable day (inclusive, compared by calendar day). */
  max?: Date | null;
  today?: Date;
}

/** Blocked by a lower limit (future-only window, or before `min`). */
export function blockedBelow(day: Date, { bounds = 'all', min, today = new Date() }: DayLimits): boolean {
  if (bounds === 'future' && dayKey(day) < dayKey(today)) return true;
  return !!min && dayKey(day) < dayKey(min);
}

/** Blocked by an upper limit (past-only window, or after `max`). */
export function blockedAbove(day: Date, { bounds = 'all', max, today = new Date() }: DayLimits): boolean {
  if (bounds === 'past' && dayKey(day) > dayKey(today)) return true;
  return !!max && dayKey(day) > dayKey(max);
}

export const isDayDisabled = (day: Date, limits: DayLimits): boolean =>
  blockedBelow(day, limits) || blockedAbove(day, limits);

// ── Months on show (ST-00294: the desktop range calendar shows two) ─────────

/** `day` falls in one of the `count` months starting with `first`. */
export function monthShown(day: Date, first: Date, count = 1): boolean {
  const offset =
    (day.getFullYear() - first.getFullYear()) * 12 + (day.getMonth() - first.getMonth());
  return offset >= 0 && offset < count;
}

/**
 * The first month a calendar of `count` months shows so that `day` is on show: its own month,
 * except where the month after it is wholly outside the limits (a 'past' picker in the current
 * month) and the month before is not — then `day`'s month goes on the right, so both months
 * shown can be chosen from.
 */
export function firstMonthFor(day: Date, count: number, limits: DayLimits = {}): Date {
  const own = startOfMonth(day);
  if (count < 2) return own;
  const before = addMonths(own, -1);
  if (blockedAbove(addMonths(own, 1), limits) && !blockedBelow(endOfMonth(before), limits)) {
    return before;
  }
  return own;
}

// ── Month grid ──────────────────────────────────────────────────────────────

/** Column headers, Sunday first — the week the app's calendars have always shown. */
export const WEEKDAYS: ReadonlyArray<{ short: string; long: string }> = [
  { short: 'Su', long: 'Sunday' },
  { short: 'Mo', long: 'Monday' },
  { short: 'Tu', long: 'Tuesday' },
  { short: 'We', long: 'Wednesday' },
  { short: 'Th', long: 'Thursday' },
  { short: 'Fr', long: 'Friday' },
  { short: 'Sa', long: 'Saturday' },
];

/** The month as weeks of seven cells; `null` pads the days outside the month. */
export function monthMatrix(month: Date): Array<Array<Date | null>> {
  const first = startOfMonth(month);
  const days = endOfMonth(month).getDate();
  const cells: Array<Date | null> = Array.from({ length: first.getDay() }, () => null);
  for (let d = 1; d <= days; d += 1) cells.push(new Date(first.getFullYear(), first.getMonth(), d));
  while (cells.length % 7) cells.push(null);
  const weeks: Array<Array<Date | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

// ── Presets ─────────────────────────────────────────────────────────────────

export interface RangePreset {
  id: string;
  label: string;
  start: Date;
  end: Date;
}

const preset = (id: string, label: string, start: Date, end: Date): RangePreset => ({
  id,
  label,
  start: startOfDay(start),
  end: startOfDay(end),
});

/**
 * Quick ranges for the range sheet: looking back for 'past', looking ahead for 'future', both
 * for 'all'. Every preset lies inside the bounds.
 */
export function rangePresets(bounds: PickerBounds, now: Date = new Date()): RangePreset[] {
  const t = startOfDay(now);
  const monthStart = startOfMonth(t);
  const monthEnd = endOfMonth(t);
  const prevStart = addMonths(t, -1);
  const nextStart = addMonths(t, 1);
  const today = preset('today', 'Today', t, t);
  const yesterday = preset('yesterday', 'Yesterday', addDays(t, -1), addDays(t, -1));
  const tomorrow = preset('tomorrow', 'Tomorrow', addDays(t, 1), addDays(t, 1));
  const last7 = preset('last7', 'Last 7 days', addDays(t, -6), t);
  const last30 = preset('last30', 'Last 30 days', addDays(t, -29), t);
  const next7 = preset('next7', 'Next 7 days', t, addDays(t, 6));
  const next30 = preset('next30', 'Next 30 days', t, addDays(t, 29));
  const lastMonth = preset('lastMonth', 'Last month', prevStart, endOfMonth(prevStart));
  const nextMonth = preset('nextMonth', 'Next month', nextStart, endOfMonth(nextStart));
  if (bounds === 'past') {
    return [today, yesterday, last7, last30, preset('thisMonth', 'This month', monthStart, t), lastMonth];
  }
  if (bounds === 'future') {
    return [today, tomorrow, next7, next30, preset('thisMonth', 'Rest of this month', t, monthEnd), nextMonth];
  }
  return [today, last7, next7, preset('thisMonth', 'This month', monthStart, monthEnd), lastMonth, nextMonth];
}

/** Quick days for a one-day picker (Plan Board): yesterday / today / tomorrow within the bounds. */
export function dayPresets(bounds: PickerBounds, now: Date = new Date()): RangePreset[] {
  const t = startOfDay(now);
  const all = [
    preset('yesterday', 'Yesterday', addDays(t, -1), addDays(t, -1)),
    preset('today', 'Today', t, t),
    preset('tomorrow', 'Tomorrow', addDays(t, 1), addDays(t, 1)),
  ];
  return all.filter((p) => !isDayDisabled(p.start, { bounds, today: t }));
}

export const presetMatches = (p: RangePreset, start: Date | null, end: Date | null): boolean =>
  sameDay(p.start, start) && sameDay(p.end, end);

// ── Range selection (sheet and desktop popup) ───────────────────────────────

export type RangeStep = 'start' | 'end';

export interface RangeState {
  start: Date;
  end: Date;
  step: RangeStep;
}

/**
 * A tapped / clicked day applied to a pending range: while choosing the start the day becomes
 * the start (the end follows when it would fall before it) and the end is next; while choosing
 * the end a later day completes the range, and an earlier one becomes the new start (a typed end
 * before the start says "End date must be after start date" instead).
 */
export function pickRangeDay(state: RangeState, day: Date): RangeState {
  const d = startOfDay(day);
  if (state.step === 'start') {
    return { start: d, end: dayKey(state.end) < dayKey(d) ? d : state.end, step: 'end' };
  }
  if (dayKey(d) < dayKey(state.start)) return { start: d, end: state.end, step: 'end' };
  return { start: state.start, end: d, step: 'start' };
}

// ── Time ────────────────────────────────────────────────────────────────────

export const pad2 = (n: number): string => String(n).padStart(2, '0');

export interface HourMinute {
  h: number;
  m: number;
}

/** "HH:MM" → hours and minutes; anything unreadable gives the fallback. */
export function parseHm(value: string | null | undefined, fallback: HourMinute = { h: 7, m: 0 }): HourMinute {
  const match = /^(\d{1,2}):(\d{1,2})/.exec(value || '');
  if (!match) return fallback;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return fallback;
  return { h, m };
}

export const formatHm = ({ h, m }: HourMinute): string => `${pad2(h)}:${pad2(m)}`;

/** `day`'s calendar date at the given wall-clock time. */
export const atTime = (day: Date, { h, m }: HourMinute): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m);

export const timeOf = (d: Date): HourMinute => ({ h: d.getHours(), m: d.getMinutes() });

export const sameTime = (a: HourMinute, b: HourMinute): boolean => a.h === b.h && a.m === b.m;

/** Same calendar day and the same hour and minute (seconds ignored). */
export const sameMinute = (a: Date | null | undefined, b: Date | null | undefined): boolean =>
  !!a && !!b && sameDay(a, b) && sameTime(timeOf(a), timeOf(b));

// ── Time chooser (ST-00264) ─────────────────────────────────────────────────
//
// The phone sheets choose a time in two taps — an hour from 00–23, then a minute from the
// 5-minute grid — and take any other minute typed ("1435", "2:35 pm"). Nothing wraps round
// midnight and nothing in the chooser changes the date; only a quick time can (Tomorrow 06:00).

/** The minutes on the chooser's grid: 00, 05 … 55 for a 5-minute step. */
export const minuteGrid = (step = 5): number[] =>
  Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);

/** The grid minute nearest to `m`, within the same hour (07 → 05, 08 → 10, 58 → 55). */
export const nearestGridMinute = (m: number, step = 5): number =>
  Math.min(Math.round(m / step) * step, (Math.ceil(60 / step) - 1) * step);

/**
 * A typed time of day → hours and minutes, or null when it is not one. Reads 24-hour digits
 * with or without a separator — 1435, 935, 7, 14:35, 14.35, 14h35, 14 35 — and 12-hour times
 * with am / pm (2:35 pm, 2pm, 12 am = 00:00, 12:30 p.m. = 12:30). 24:00 and later are not a
 * time of day.
 */
export function parseTypedTime(text: string | null | undefined): HourMinute | null {
  const s = String(text ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!s) return null;
  const meridiem = /^(.*?)\s*([ap])\.?(?:m\.?)?$/.exec(s);
  const body = meridiem ? meridiem[1]!.trim() : s;
  const half = meridiem ? meridiem[2] : null;

  let h: number;
  let m: number;
  const split = /^(\d{1,2})\s*[:.h ]\s*(\d{2})?$/.exec(body);
  if (split) {
    h = Number(split[1]);
    m = split[2] ? Number(split[2]) : 0;
  } else if (/^\d{1,4}$/.test(body)) {
    // 7 / 14 → the hour; 935 → 9:35; 1435 → 14:35.
    h = Number(body.length <= 2 ? body : body.slice(0, -2));
    m = body.length <= 2 ? 0 : Number(body.slice(-2));
  } else {
    return null;
  }
  if (m > 59) return null;
  if (half) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (half === 'p' ? 12 : 0);
  } else if (h > 23) {
    return null;
  }
  return { h, m };
}

// ── Typed dates (ST-00294: the desktop popup's text fields) ─────────────────

/** "2026/09/27" from the local calendar date — what the desktop text fields show. */
export const formatTypedDate = (d: Date): string =>
  `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;

/** "2026/09/27 06:00" */
export const formatTypedDateTime = (d: Date): string =>
  `${formatTypedDate(d)} ${formatHm(timeOf(d))}`;

/**
 * A typed day → that local day (midnight), or null. Reads YYYY/MM/DD, YYYY-MM-DD, DD/MM/YYYY and
 * DD-MM-YYYY with one- or two-digit days and months. A day that does not exist (2026/02/30,
 * month 13) is not a day — it is not rolled over into the next month.
 */
export function parseTypedDate(text: string | null | undefined): Date | null {
  const s = String(text ?? '').trim();
  const iso = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(s);
  const dmy = iso ? null : /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (!iso && !dmy) return null;
  const [y, m, d] = iso
    ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    : [Number(dmy![3]), Number(dmy![2]), Number(dmy![1])];
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

/**
 * A typed date with an optional time → Date, or null: the date as parseTypedDate reads it, then
 * after a space, a "T" or a comma the time as parseTypedTime reads it (2026/09/27 14:35,
 * 27/09/2026 1435, 2026-09-27T06:00). Without a time the day gets `fallbackTime`; a time that
 * cannot be read makes the whole entry unreadable.
 */
export function parseTypedDateTime(
  text: string | null | undefined,
  fallbackTime: HourMinute
): Date | null {
  const match = /^([\d/-]+)(?:(?:\s*[Tt,]\s*|\s+)(.+))?$/.exec(String(text ?? '').trim());
  if (!match) return null;
  const day = parseTypedDate(match[1]);
  if (!day) return null;
  const time = match[2] === undefined ? fallbackTime : parseTypedTime(match[2]);
  return time ? atTime(day, time) : null;
}

// ── Quick times (ST-00264) ──────────────────────────────────────────────────

/** A chip that picks a date and time worked out from the current instant ("+30 min"). */
export interface TimePreset {
  id: string;
  /** Chip text, e.g. "+30 min". */
  label: string;
  /** Print the time it picks on the chip (off where the label says it: "Tomorrow 06:00"). */
  showTime?: boolean;
  /** The date and time the chip picks, from `now` (a picker value: local wall-clock digits). */
  resolve: (now: Date) => Date;
}

export interface ResolvedTimePreset {
  id: string;
  label: string;
  /** The time it picks as printed on the chip ("14:40", "Mon 01:10"), or null. */
  detail: string | null;
  at: Date;
}

const MINUTE_MS = 60_000;

/** `d` moved up to the next whole minute (a time already on the minute stays). */
export const ceilToMinute = (d: Date): Date =>
  new Date(Math.ceil(d.getTime() / MINUTE_MS) * MINUTE_MS);

/** `d` moved up onto the next `step`-minute mark (14:07 → 14:10, 14:58 → 15:00). */
export function ceilToStep(d: Date, step = 5): Date {
  const x = ceilToMinute(d);
  const up = Math.ceil(x.getMinutes() / step) * step;
  return new Date(x.getFullYear(), x.getMonth(), x.getDate(), x.getHours(), up);
}

/** `now` plus `minutes`, moved up onto the 5-minute grid (14:07 + 30 → 14:40). */
const fromNow = (minutes: number) => (now: Date) =>
  ceilToStep(new Date(now.getTime() + minutes * MINUTE_MS));

/**
 * The quick times of a planning field (Plan & Dispatch's planned start): Now (the next whole
 * minute), +30 min / +1 h / +2 h on the 5-minute grid, and 06:00 on the next SAST day.
 */
export const PLANNING_TIME_PRESETS: readonly TimePreset[] = [
  { id: 'now', label: 'Now', resolve: ceilToMinute },
  { id: 'in30m', label: '+30 min', resolve: fromNow(30) },
  { id: 'in1h', label: '+1 h', resolve: fromNow(60) },
  { id: 'in2h', label: '+2 h', resolve: fromNow(120) },
  {
    id: 'tomorrow0600',
    label: 'Tomorrow 06:00',
    showTime: false,
    resolve: (now) => atTime(addDays(miningDayOf(now), 1), { h: 6, m: 0 }),
  },
];

/** Back to the present, for a look-up "at" field (who was driving at …). */
export const NOW_TIME_PRESETS: readonly TimePreset[] = [PLANNING_TIME_PRESETS[0]!];

/**
 * The presets worked out for `now`, keeping only those inside the picker's limits: the days
 * `min` / `max` / `bounds` allow and, to the minute, nothing later than now for 'past' and
 * nothing earlier than now for 'future'.
 */
export function resolveTimePresets(
  presets: readonly TimePreset[],
  { bounds = 'all', min = null, max = null }: DayLimits = {},
  now: Date = new Date()
): ResolvedTimePreset[] {
  const floorNow = Math.floor(now.getTime() / MINUTE_MS) * MINUTE_MS;
  const ceilNow = ceilToMinute(now).getTime();
  return presets
    .map((p) => {
      const at = p.resolve(now);
      const time = formatHm(timeOf(at));
      const detail =
        p.showTime === false ? null : sameDay(at, now) ? time : `${formatPickerWeekday(at)} ${time}`;
      return { id: p.id, label: p.label, detail, at };
    })
    .filter(({ at }) => {
      if (isDayDisabled(at, { bounds, min, max, today: now })) return false;
      if (bounds === 'past' && at.getTime() > ceilNow) return false;
      if (bounds === 'future' && at.getTime() < floorNow) return false;
      return true;
    });
}

// ── datetime-local strings (UnifiedDateTimeRangePicker's value format) ──────

/** "YYYY-MM-DDTHH:MM" (or any string `new Date()` reads) → Date, else null. */
export function parseLocalValue(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date → "YYYY-MM-DDTHH:MM" in local time; null → ''. */
export const toLocalValue = (d: Date | null | undefined): string =>
  d
    ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    : '';

// ── Labels (app format, MINING_TZ) ──────────────────────────────────────────

let offsetFormat: Intl.DateTimeFormat | null = null;

/** MINING_TZ's offset from UTC at instant `t`, in ms (SAST: +2 h all year). */
function miningOffsetMs(t: number): number {
  offsetFormat ??= new Intl.DateTimeFormat('en-US', {
    timeZone: MINING_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = offsetFormat.formatToParts(new Date(t));
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return wall - Math.floor(t / 1000) * 1000;
}

/** The instant whose MINING_TZ wall clock shows `d`'s local digits (see the header note). */
export function asMiningWallClock(d: Date): Date {
  const digits = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes());
  return new Date(digits - miningOffsetMs(digits));
}

/**
 * The MINING_TZ calendar day of instant `t`, as a picker day (local midnight with those
 * digits): "tomorrow" for a quick time follows the SAST calendar.
 */
export function miningDayOf(t: Date): Date {
  const wall = new Date(t.getTime() + miningOffsetMs(t.getTime()));
  return new Date(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate());
}

/** "Mon" — a quick time that lands on another day. */
export const formatPickerWeekday = (d: Date): string =>
  formatMiningDate(asMiningWallClock(d), {
    weekday: 'short',
    year: undefined,
    month: undefined,
    day: undefined,
  });

/** "2026/09/27" */
export const formatPickerDate = (d: Date | null | undefined): string =>
  d ? formatMiningDate(asMiningWallClock(d)) : '';

/** "Sun, 2026/09/27" — a single day, where the weekday helps. */
export const formatPickerDay = (d: Date | null | undefined): string =>
  d ? formatMiningDate(asMiningWallClock(d), { weekday: 'short' }) : '';

/** "2026/09/27 06:00" */
export const formatPickerDateTime = (d: Date | null | undefined): string =>
  d ? `${formatPickerDate(d)} ${formatMiningTime(asMiningWallClock(d))}` : '';

/** "September 2026" — the calendar heading. */
export const formatMonthYear = (d: Date): string =>
  formatMiningDate(asMiningWallClock(startOfMonth(d)), { month: 'long', day: undefined });

/** "Sunday, 27 September 2026" — a day cell's accessible name. */
export const formatDayLong = (d: Date): string => formatMiningDateLong(asMiningWallClock(d));
